import { getLocale, translate, translateRich, useLocale } from "../../localization";
import React, { useEffect, useState } from "react";
import {
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { signOut, updatePassword, fetchUserAttributes } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import PersonIcon from "@mui/icons-material/Person";
import { AppButton } from "../../components/ui";
import AccountsAPI from "../../services/AccountsAPI";
import {
  formatSubscriptionDate,
  formatSubscriptionLabel,
  getRoleDisplayName,
} from "../../utility/subscriptionAccess";
import "./Account.scss";
import { getAccountPlanName } from "../../utility/billingPlans";
import { hasAccountPermission } from "../../utility/accountPermissions";

const formatInvoiceDate = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(getLocale(), {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsedDate);
};

const formatInvoiceAmount = (amount, currency) => {
  if (amount === null || amount === undefined || !currency) {
    return null;
  }

  try {
    return new Intl.NumberFormat(getLocale(), {
      style: "currency",
      currency: String(currency).toUpperCase(),
    }).format(amount / 100);
  } catch (error) {
    return `${(amount / 100).toFixed(2)} ${String(currency).toUpperCase()}`;
  }
};

const AccountPage = () => {
  const locale = useLocale();
  const navigate = useNavigate();
  const auth = useAuth();
  const planName = getAccountPlanName(auth);
  const canViewContent = auth.permissions?.viewWebContent === true;
  const canManageJoinCodes = auth.permissions?.manageJoinCodes === true;
  const canViewAnalytics = hasAccountPermission(auth.permissions, "viewAnalytics");
  const subscriptionLabel = formatSubscriptionLabel(
    auth.subscriptionStatus,
    auth.hasActiveSubscription
  );
  const hasStripeManagedSubscription =
    auth.subscriptionSource === "stripe" &&
    auth.subscriptionStatus === "subscribed" &&
    auth.hasActiveSubscription;
  const showBillingSection = auth.subscriptionSource === "stripe";
  const isSubscribedUser =
    auth.subscriptionStatus === "subscribed" || auth.subscriptionStatus === "perpetual";
  // User email
  const [userEmail, setUserEmail] = useState("");
  const [billingError, setBillingError] = useState("");
  const [billingSuccess, setBillingSuccess] = useState("");
  const [isUpdatingAutoRenew, setIsUpdatingAutoRenew] = useState(false);
  const [billingInvoices, setBillingInvoices] = useState([]);
  const [isBillingInvoicesLoading, setIsBillingInvoicesLoading] = useState(false);
  const [billingInvoicesError, setBillingInvoicesError] = useState("");

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  // Logout confirmation dialog
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch user email
  useEffect(() => {
    const getUserEmail = async () => {
      try {
        const attributes = await fetchUserAttributes();
        setUserEmail(attributes.email || "");
      } catch (err) {
        console.error("Error fetching user attributes:", err);
      }
    };
    getUserEmail();
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadBillingInvoices = async () => {
      if (!auth.userId || !showBillingSection) {
        setBillingInvoices([]);
        setBillingInvoicesError("");
        setIsBillingInvoicesLoading(false);
        return;
      }

      setIsBillingInvoicesLoading(true);
      setBillingInvoicesError("");

      try {
        const response = await AccountsAPI.get(
          `/users/${auth.userId}/billing/invoices`,
          { requiresAuth: true }
        );
        const payload = response?.data?.data ?? response?.data ?? {};

        if (!isCancelled) {
          setBillingInvoices(payload.invoices || []);
        }
      } catch (error) {
        console.error("Failed to load billing invoices", error);

        if (!isCancelled) {
          setBillingInvoices([]);
          setBillingInvoicesError(
            error?.response?.data?.message ||
              translate("Unable to load billing history right now.")
          );
        }
      } finally {
        if (!isCancelled) {
          setIsBillingInvoicesLoading(false);
        }
      }
    };

    loadBillingInvoices();

    return () => {
      isCancelled = true;
    };
  }, [auth.userId, showBillingSection, locale]);

  const handleLogout = async () => {
    try {
      await signOut();
      console.log("User logged out successfully.");
      navigate("/login");
    } catch (err) {
      console.error("Error during logout:", err);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError("");
    setPasswordSuccess("");

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(translate("Please fill in all fields."));
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError(translate("New password must be at least 8 characters."));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(translate("New passwords do not match."));
      return;
    }

    setChangingPassword(true);

    try {
      await updatePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
      });
      setPasswordSuccess(translate("Password changed successfully!"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordSuccess("");
      }, 2000);
    } catch (err) {
      console.error("Error changing password:", err);
      if (err.name === "NotAuthorizedException") {
        setPasswordError(translate("Current password is incorrect."));
      } else if (err.name === "InvalidPasswordException") {
        setPasswordError(translate("New password does not meet requirements."));
      } else {
        setPasswordError(err.message || translate("Failed to change password."));
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const cancelPasswordChange = () => {
    setShowPasswordForm(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handleAutoRenewUpdate = async (enableAutoRenew) => {
    setBillingError("");
    setBillingSuccess("");
    setIsUpdatingAutoRenew(true);

    try {
      await AccountsAPI.post(
        enableAutoRenew
          ? "/users/subscription/resume"
          : "/users/subscription/cancel",
        {},
        { requiresAuth: true }
      );
      await auth.refreshAuthSession();
      setBillingSuccess(
        enableAutoRenew
          ? translate("Auto-renew is back on. Your subscription will renew automatically at the end of the current term.")
          : translate("Auto-renew has been turned off. Your plan stays active until the end of the current term.")
      );
    } catch (error) {
      console.error("Failed to update subscription auto-renew", error);
      setBillingError(
        error?.response?.data?.message ||
          translate("Unable to update auto-renew right now. Please try again.")
      );
    } finally {
      setIsUpdatingAutoRenew(false);
    }
  };

  return (
    <div className="accountPage">
      {/* Header */}
      <div className="accountHeader">
        <h1 className="accountTitle">{translate("Account")}</h1>
      </div>

      {hasStripeManagedSubscription &&
        !auth.subscriptionRenews &&
        auth.subscriptionEndsAt && (
          <Alert severity="warning" className="accountTopNotice">{translateRich("{value0} Your subscription expires on {value1} unless you turn it back on before then.", {
  value0: <strong>{translate("Auto-renew is off.")}</strong>,
  value1: formatSubscriptionDate(auth.subscriptionEndsAt)
})}</Alert>
        )}

      {/* Profile Section */}
      <div className="accountSection">
        <div className="accountProfile">
          <div className="accountAvatar">
            <PersonIcon className="avatarIcon" />
          </div>
          <div className="accountProfileInfo">
            <div className="accountName">{auth.organizationName || translate("User")}</div>
            <div className="accountEmail">{userEmail || "—"}</div>
          </div>
        </div>
      </div>

      <div className="accountSection">
        <div className="accountSectionTitle">{translate("License")}</div>
        {billingError && <Alert severity="error" sx={{ mb: 2 }}>{billingError}</Alert>}
        {billingSuccess && <Alert severity="success" sx={{ mb: 2 }}>{billingSuccess}</Alert>}
        <div className="accountPlanSection">
          <div className="accountPlanSummary">
            <div className={`accountPlanBadge accountPlanBadge--${auth.hasActiveSubscription ? "active" : "inactive"}`}>
              {subscriptionLabel}
            </div>
            <div className="accountPlanTitle">
              {planName}
            </div>
            <div className="accountPlanMeta">
              {canViewContent ? translate("Web content access is active.") : translate("Web content access is inactive.")}
            </div>
            <div className="accountPlanMeta">
              {canViewAnalytics ? translate("Educator analytics are available.") : translate("Analytics are not included.")}
              {" "}{canManageJoinCodes ? translate("Student join codes are available.") : translate("Join codes are not included.")}
            </div>
            {auth.permissions?.viewVrContent === true && (
              <div className="accountPlanMeta">
                {String(auth.roleName).toLowerCase() === "educatorsite"
                  ? translate("Unlimited web and VR access for your site.")
                  : auth.educatorTrialExpiresAt && new Date(auth.educatorTrialExpiresAt) > new Date()
                  ? translate("Trial VR access through {value0}.", {
  value0: formatSubscriptionDate(auth.educatorTrialExpiresAt)
})
                  : translate("VR content access is active.")}
              </div>
            )}
            {auth.subscriptionBillingInterval && hasStripeManagedSubscription && (
              <div className="accountPlanMeta">{translateRich("Billed {value0}.", {
  value0: auth.subscriptionBillingInterval === "month" ? "monthly" : "annually"
})}</div>
            )}
            <div className="accountPlanMeta">{translateRich("Role: {value0}", {
  value0: getRoleDisplayName({
    roleName: auth.roleName,
    roleId: auth.roleId,
    isRoleNameLoading: auth.isAccountProfileLoading
  })
})}</div>
            {auth.hasActiveSubscription && auth.subscriptionEndsAt && (
              <div className="accountPlanMeta">
                {hasStripeManagedSubscription && auth.subscriptionRenews
                  ? translate("Renews on {value0}", {
  value0: formatSubscriptionDate(auth.subscriptionEndsAt)
})
                  : translate("Access through {value0}", {
  value0: formatSubscriptionDate(auth.subscriptionEndsAt)
})}
              </div>
            )}
            {hasStripeManagedSubscription && (
              <div className="accountPlanMeta">{translateRich("Auto-renew: {value0}", {
  value0: auth.subscriptionRenews ? translate("On") : translate("Off")
})}</div>
            )}
          </div>
          <div className="accountPlanActions">
            {!isSubscribedUser && (
              <AppButton
                className="accountBtn"
                kind="primary"
                onClick={() => navigate("/upgrade")}
              >{translate("View Plans")}</AppButton>
            )}
            {hasStripeManagedSubscription && (
              <AppButton
                className="accountBtn"
                kind={auth.subscriptionRenews ? "danger" : "secondary"}
                loading={isUpdatingAutoRenew}
                loadingLabel={translate("Updating...")}
                onClick={() => handleAutoRenewUpdate(!auth.subscriptionRenews)}
              >
                {auth.subscriptionRenews
                  ? translate("Turn Off Auto-Renew")
                  : translate("Turn On Auto-Renew")}
              </AppButton>
            )}
          </div>
        </div>
      </div>

      {showBillingSection && (
        <div className="accountSection">
          <div className="accountSectionTitle">{translate("Billing history")}</div>
          {billingInvoicesError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {billingInvoicesError}
            </Alert>
          )}
          {isBillingInvoicesLoading ? (
            <div className="accountBillingEmptyState">{translate("Loading invoices...")}</div>
          ) : billingInvoices.length ? (
            <div className="accountInvoicesList">
              {billingInvoices.map((invoice) => {
                const amountLabel =
                  formatInvoiceAmount(invoice.amountPaid, invoice.currency) ||
                  formatInvoiceAmount(invoice.amountDue, invoice.currency) ||
                  translate("Invoice");
                const issuedDate = formatInvoiceDate(invoice.createdAt);
                const paidDate = formatInvoiceDate(invoice.paidAt);

                return (
                  <div className="accountInvoiceRow" key={invoice.id}>
                    <div className="accountInvoiceSummary">
                      <div className="accountInvoiceTitle">
                        {amountLabel}
                        {invoice.status ? ` · ${invoice.status}` : ""}
                      </div>
                      <div className="accountInvoiceMeta">
                        {issuedDate ? translate("Issued {value0}", {
  value0: issuedDate
}) : translate("Stripe invoice")}
                        {paidDate ? translate(" \xB7 Paid {value0}", {
  value0: paidDate
}) : ""}
                        {invoice.number ? ` · ${invoice.number}` : ""}
                      </div>
                    </div>
                    <div className="accountInvoiceActions">
                      {invoice.hostedInvoiceUrl && (
                        <AppButton
                          className="accountBtn"
                          kind="secondary"
                          component="a"
                          href={invoice.hostedInvoiceUrl}
                          target="_blank"
                          rel="noreferrer"
                        >{translate("View invoice")}</AppButton>
                      )}
                      {invoice.invoicePdfUrl && (
                        <AppButton
                          className="accountBtn"
                          kind="ghost"
                          component="a"
                          href={invoice.invoicePdfUrl}
                          target="_blank"
                          rel="noreferrer"
                        >{translate("PDF")}</AppButton>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="accountBillingEmptyState">{translate("Invoices will appear here after a payment is charged.")}</div>
          )}
        </div>
      )}

      {/* Security Section */}
      <div className="accountSection">
        <div className="accountSectionTitle">{translate("Security")}</div>
        <div className="accountSettingsList">
          <div className="accountSettingsItem">
            <div className="accountSettingsInfo">
              <div className="accountSettingsLabel">{translate("Password")}</div>
              <div className="accountSettingsDesc">
                {showPasswordForm ? translate("Enter your current and new password") : translate("Change your account password")}
              </div>
            </div>
            {!showPasswordForm && (
              <AppButton
                className="accountBtn"
                kind="secondary"
                onClick={() => setShowPasswordForm(true)}
              >{translate("Change Password")}</AppButton>
            )}
          </div>

          {showPasswordForm && (
            <div className="passwordForm">
              <TextField
                label={translate("Current Password")}
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label={translate("New Password")}
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                size="small"
                helperText={translate("Must be at least 8 characters")}
              />
              <TextField
                label={translate("Confirm New Password")}
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                size="small"
              />

              {passwordError && (
                <div className="accountMessage error">{passwordError}</div>
              )}
              {passwordSuccess && (
                <div className="accountMessage success">{passwordSuccess}</div>
              )}

              <div className="passwordFormActions">
                <AppButton
                  className="accountBtn"
                  kind="primary"
                  onClick={handlePasswordChange}
                  disabled={changingPassword}
                >
                  {changingPassword ? translate("Saving...") : translate("Save Password")}
                </AppButton>
                <AppButton
                  className="accountBtn"
                  kind="ghost"
                  onClick={cancelPasswordChange}
                  disabled={changingPassword}
                >{translate("Cancel")}</AppButton>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sign Out Section */}
      <div className="accountSection accountDangerZone">
        <div className="accountLogoutSection">
          <div className="accountLogoutInfo">
            <div className="accountLogoutTitle">{translate("Sign out")}</div>
            <div className="accountLogoutDesc">{translate("You will need to sign in again to access your dashboard")}</div>
          </div>
          <AppButton
            className="accountBtnDanger"
            kind="danger"
            onClick={() => setLogoutDialogOpen(true)}
          >{translate("Sign Out")}</AppButton>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{ className: "accountDialogPaper" }}
      >
        <DialogTitle>{translate("Sign Out")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{translate("Are you sure you want to sign out of your account?")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton className="accountBtn" kind="ghost" onClick={() => setLogoutDialogOpen(false)}>{translate("Cancel")}</AppButton>
          <AppButton className="accountBtnDanger" kind="danger" onClick={handleLogout}>{translate("Sign Out")}</AppButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AccountPage;
